<template>
  <div
    class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 dark:bg-background min-h-screen relative"
  >
    <!-- Coming Soon Watermark -->
    <div
      class="absolute inset-0 bg-background/80 backdrop-blur-[2px] flex items-center justify-center z-10 pointer-events-none"
    >
      <div
        class="text-[120px] font-bold text-foreground/10 transform -rotate-12 whitespace-nowrap"
      >
        COMING SOON
      </div>
    </div>

    <div class="mb-8">
      <h1 class="text-3xl font-bold text-foreground mb-2">Feed</h1>
      <p class="text-muted-foreground">
        Stay updated with the latest from the community
      </p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div class="lg:col-span-2 space-y-6">
        <div class="bg-card rounded-lg shadow p-6 border border-border">
          <div class="flex items-start space-x-4">
            <img
              :src="userAvatar"
              alt="User Avatar"
              class="w-12 h-12 rounded-full object-cover"
            />
            <div class="flex-1">
              <textarea
                v-model="newPost"
                placeholder="What's on your mind?"
                rows="3"
                class="w-full border border-input rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-ring resize-none bg-background text-foreground placeholder:text-muted-foreground"
              ></textarea>
              <div class="flex justify-between items-center mt-3">
                <div class="flex space-x-3">
                  <button
                    class="text-muted-foreground hover:text-primary flex items-center"
                  >
                    <svg
                      class="w-5 h-5 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Photo
                  </button>
                  <button
                    class="text-muted-foreground hover:text-primary flex items-center"
                  >
                    <svg
                      class="w-5 h-5 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Video
                  </button>
                </div>
                <button
                  @click="createPost"
                  class="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
                  :disabled="!newPost.trim()"
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Feed Posts -->
        <div
          v-for="post in feedPosts"
          :key="post.id"
          class="bg-card rounded-lg shadow border border-border"
        >
          <div class="p-6 pb-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <img
                  :src="post.author.avatar"
                  alt="Author Avatar"
                  class="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h3 class="font-semibold text-foreground">
                    {{ post.author.name }}
                  </h3>
                  <p class="text-sm text-muted-foreground">
                    {{ post.author.title }} · {{ formatDate(post.created_at) }}
                  </p>
                </div>
              </div>
              <button class="text-muted-foreground hover:text-foreground">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="px-6 pb-3">
            <p class="text-foreground whitespace-pre-wrap">
              {{ post.content }}
            </p>
            <p v-if="post.hashtags" class="mt-2 text-primary">
              {{ post.hashtags }}
            </p>
          </div>

          <!-- Post Image (if any) -->
          <div v-if="post.image" class="px-6 pb-3">
            <img :src="post.image" alt="Post image" class="w-full rounded-lg" />
          </div>

          <!-- Post Actions -->
          <div class="px-6 py-3 border-t border-border">
            <div class="flex justify-between text-muted-foreground">
              <button
                @click="likePost(post.id)"
                class="flex items-center space-x-2 hover:text-primary"
                :class="{ 'text-primary': post.liked }"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  />
                </svg>
                <span>{{ post.likes }}</span>
              </button>
              <button class="flex items-center space-x-2 hover:text-primary">
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <span>{{ post.comments }}</span>
              </button>
              <button class="flex items-center space-x-2 hover:text-primary">
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Load More -->
        <div class="text-center py-4">
          <button
            @click="loadMorePosts"
            class="px-6 py-2 text-primary hover:bg-muted rounded-lg font-medium"
          >
            Load More Posts
          </button>
        </div>
      </div>

      <!-- Right Sidebar -->
      <div class="space-y-6">
        <!-- Trending Topics -->
        <div class="bg-card rounded-lg shadow p-6 border border-border">
          <h3 class="font-bold text-lg mb-4">Trending Topics</h3>
          <div class="space-y-3">
            <div
              v-for="topic in trendingTopics"
              :key="topic.id"
              class="hover:bg-muted/50 p-2 rounded cursor-pointer"
            >
              <p class="font-semibold text-foreground">{{ topic.name }}</p>
              <p class="text-sm text-muted-foreground">
                {{ topic.posts }} posts
              </p>
            </div>
          </div>
        </div>

        <!-- Suggested Connections -->
        <div class="bg-card rounded-lg shadow p-6 border border-border">
          <h3 class="font-bold text-lg mb-4">People to Follow</h3>
          <div class="space-y-4">
            <div
              v-for="person in suggestedConnections"
              :key="person.id"
              class="flex items-center justify-between"
            >
              <div class="flex items-center space-x-3">
                <img
                  :src="person.avatar"
                  alt="Avatar"
                  class="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p class="font-semibold text-sm text-foreground">
                    {{ person.name }}
                  </p>
                  <p class="text-xs text-muted-foreground">
                    {{ person.title }}
                  </p>
                </div>
              </div>
              <button
                @click="followUser(person.id)"
                class="px-3 py-1 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90"
              >
                Follow
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

export default {
  name: "FeedsView",
  data() {
    return {
      newPost: "",
      feedPosts: [
        {
          id: 1,
          author: {
            name: "Lisa Balogon",
            title: "UX/UI Designer",
            avatar: require("@/assets/users/default-profile-picture.jpg"),
          },
          content:
            "The UI/UX Design course on the TCC learning portal has been helpful so far. Day 8 of my self-improvement journey #UI/UX #Community",
          hashtags: "#UI/UX #Community",
          created_at: "2026-01-20T10:00:00",
          likes: 100,
          comments: 28,
          liked: false,
        },
      ],
      trendingTopics: [
        { id: 1, name: "#WebDevelopment", posts: 1245 },
        { id: 2, name: "#DesignThinking", posts: 890 },
        { id: 3, name: "#ProductManagement", posts: 654 },
        { id: 4, name: "#DataScience", posts: 521 },
      ],
      suggestedConnections: [
        {
          id: 1,
          name: "Lisa Balogon",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 2,
          name: "John Doe",
          title: "Frontend Developer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
      ],
    };
  },
  computed: {
    userAvatar() {
      const user = this.$store.state.user;
      return (
        user?.profile_picture ||
        require("@/assets/users/default-profile-picture.jpg")
      );
    },
  },
  methods: {
    createPost() {
      if (!this.newPost.trim()) return;

      const post = {
        id: Date.now(),
        author: {
          name: this.$store.state.user?.name || "User",
          title: this.$store.state.user?.role || "Member",
          avatar: this.userAvatar,
        },
        content: this.newPost,
        created_at: new Date().toISOString(),
        likes: 0,
        comments: 0,
        liked: false,
      };

      this.feedPosts.unshift(post);
      this.newPost = "";
      this.$toast.success("Post created successfully!");
    },
    likePost(postId) {
      const post = this.feedPosts.find((p) => p.id === postId);
      if (post) {
        post.liked = !post.liked;
        post.likes += post.liked ? 1 : -1;
      }
    },
    followUser(userId) {
      this.$toast.success("User followed successfully!");
      this.suggestedConnections = this.suggestedConnections.filter(
        (person) => person.id !== userId,
      );
    },
    loadMorePosts() {
      // Implement pagination
      console.log("Loading more posts...");
    },
    formatDate(date) {
      return dayjs(date).fromNow();
    },
  },
  mounted() {
    window.scrollTo(0, 0);
  },
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
